import type { DivergenceRow } from "../api/rest";
import type { RackVolume } from "../scene/rackVolume";
import type { OccupancyCell } from "../scene/OccupancyLayer";

/**
 * ADR 195: the coverage grid — the run's per-address scan status laid out as the
 * physical rack face (columns = positions along the aisle, rows = levels). The
 * skeleton comes from `structure/volumes` (run-independent: every bound bin,
 * ADR 188 identity), the paint from the run's occupancy cells (the SAME payload
 * the 3D map layer renders) and the WMS divergence rows (the SAME rows the
 * reconciliation table shows) — a grid cell and the map can never disagree.
 * Pure (no React) so the join and ordering are unit-tested.
 */

export type CoverageCellStatus = "counted" | "empty" | "not_scanned" | "unidentified" | "divergence";

export interface CoverageGridCell {
  address: string;
  status: CoverageCellStatus;
}

export interface CoverageGridColumn {
  /** Physical-position identity: bound space code, else the address's dash
   *  token, else the address itself (same precedence as rackFrame.columnKey). */
  key: string;
  /** Aligned to the aisle's `levels` (desc); null = no bin at that level. */
  cells: (CoverageGridCell | null)[];
}

export interface CoverageGridAisle {
  /** Aisle label; "__none__" buckets label-less volumes (docks, manual nodes) —
   *  the caller translates the literal, never displays it raw (ADR 137 rule). */
  aisle: string;
  /** Distinct rack levels, highest first — grid rows top-down. */
  levels: number[];
  /** Columns in physical order along the aisle's dominant axis. */
  columns: CoverageGridColumn[];
  counts: Record<CoverageCellStatus, number>;
}

const spaceKey = (v: RackVolume): string => {
  const m = v.nodeId?.match(/\/space-([^/]+)$/);
  if (m) return m[1];
  const i = v.address.indexOf("-");
  return i >= 0 ? v.address.slice(i + 1) : v.address;
};

const centroidOf = (v: RackVolume): [number, number] => {
  let x = 0, y = 0;
  for (const [px, py] of v.footprint) { x += px; y += py; }
  const n = Math.max(1, v.footprint.length);
  return [x / n, y / n];
};

/** Addresses carrying any non-match WMS row — one divergent (address, product)
 *  pair marks the whole bin: the operator walks to the address, not the SKU. */
export function divergentAddresses(rows: DivergenceRow[]): Set<string> {
  const out = new Set<string>();
  for (const r of rows) if (r.status !== "match") out.add(r.address);
  return out;
}

export function buildCoverageGrid(
  volumes: RackVolume[],
  cells: OccupancyCell[],
  divRows: DivergenceRow[],
): CoverageGridAisle[] {
  const statusByAddress = new Map<string, OccupancyCell["status"]>();
  for (const c of cells) statusByAddress.set(c.address, c.status);
  const divergent = divergentAddresses(divRows);

  const byAisle = new Map<string, RackVolume[]>();
  for (const v of volumes) {
    const k = v.aisleLabel ?? "__none__";
    const arr = byAisle.get(k) ?? [];
    arr.push(v);
    byAisle.set(k, arr);
  }

  const out: CoverageGridAisle[] = [];
  for (const [aisle, vols] of byAisle) {
    const levels = [...new Set(vols.map((v) => v.level))].sort((a, b) => b - a);
    const levelIdx = new Map(levels.map((l, i) => [l, i]));

    // Columns keyed by physical position; a (key, level) collision (two bins
    // claiming the same slot — dirty master) spills into its own column keyed
    // by address rather than silently overwriting a bin.
    interface Acc { key: string; sx: number; sy: number; n: number; cells: (CoverageGridCell | null)[] }
    const cols = new Map<string, Acc>();
    for (const v of vols) {
      let key = spaceKey(v);
      if (cols.get(key)?.cells[levelIdx.get(v.level)!]) key = `${key}#${v.address}`;
      let col = cols.get(key);
      if (!col) {
        col = { key, sx: 0, sy: 0, n: 0, cells: levels.map(() => null) };
        cols.set(key, col);
      }
      const [cx, cy] = centroidOf(v);
      col.sx += cx; col.sy += cy; col.n += 1;
      const status: CoverageCellStatus = divergent.has(v.address)
        ? "divergence"
        : statusByAddress.get(v.address) ?? "not_scanned";
      col.cells[levelIdx.get(v.level)!] = { address: v.address, status };
    }

    // Physical order: project column centres on the dominant axis of their
    // spread (the aisle runs along its longer extent — same test rackFrame uses).
    const accs = [...cols.values()];
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const a of accs) {
      const x = a.sx / a.n, y = a.sy / a.n;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    const aisleX = maxX - minX >= maxY - minY;
    const columns: CoverageGridColumn[] = accs
      .map((a) => ({ key: a.key, cells: a.cells, at: aisleX ? a.sx / a.n : a.sy / a.n }))
      .sort((l, r) => (l.at - r.at) || l.key.localeCompare(r.key))
      .map(({ key, cells: c }) => ({ key, cells: c }));

    const counts: Record<CoverageCellStatus, number> = {
      counted: 0, empty: 0, not_scanned: 0, unidentified: 0, divergence: 0,
    };
    for (const col of columns) for (const c of col.cells) if (c) counts[c.status]++;

    out.push({ aisle, levels, columns, counts });
  }

  return out.sort((l, r) => {
    if (l.aisle === "__none__") return 1;
    if (r.aisle === "__none__") return -1;
    return l.aisle.localeCompare(r.aisle);
  });
}
